import { useState, useEffect, useCallback } from 'react';

import { apiService } from '@/lib/axios';

export function useTemplates({ sortBy, sortDirection, type, isDefault }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.templates.getAll({
        sortBy,
        sortDirection,
        limit: 100,
        type,
        isDefault,
      });

      if (response.data.success) {
        const transformed = response.data.data.map((template) => ({
          id: template.id,
          type: template.type,
          name: template.name,
          creator: template.creator,
          default: template.isDefault,
          canDelete: template.canDelete,
          config: template.config,
          values: template.values,
          isUsed: template.isUsed,
        }));

        setTemplates(() => transformed);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDirection, type, isDefault]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  return { templates, loading, error, refetch: loadTemplates };
}

export const normalizeTemplate = (apiData) => ({
  id: apiData.id,
  type: apiData.type,
  name: apiData.name,
  creator: apiData.creator,
  default: apiData.isDefault,
  canDelete: apiData.canDelete,
  config: apiData.config,
  values: apiData.values,
  isUsed: apiData.isUsed,
});

export const useTemplateById = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setTemplate(null);
    setError(null);
  }, []);

  const load = useCallback(async (templateId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.templates.getById(templateId);

      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Failed to load template');
      }

      const normalized = normalizeTemplate(response.data.data);

      setTemplate(normalized);

      return normalized;
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to load template';

      setError(message);

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { template, loading, error, load, reset };
};
